import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBackdropClose } from "@/lib/useBackdropClose";
import { TrafficBadge, EstadoOrden } from "@/components/Atoms";
import { ArrowLeft, Phone, At, MapPin, Crown, Check, Users } from "@phosphor-icons/react";
import DocumentosSST from "@/components/DocumentosSST";
import CronogramaBadge from "@/components/CronogramaBadge";
import SignaturePad from "@/components/SignaturePad";

const COP = (n) => new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(n || 0);
const TIPOS_PROYECTO_LABELS = {
    remodelacion: "Remodelación",
    mantenimiento_edificacion: "Mantenimiento de edificación",
    obra_nueva_tradicional: "Construcción obra nueva — Tradicional",
    steel_frame: "Construcción Steel Frame (LGSF)",
};

export default function ClientDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const [c, setC] = useState(null);
    const [sub, setSub] = useState(null);
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [showTeamModal, setShowTeamModal] = useState(false);
    const [showActaModal, setShowActaModal] = useState(false);
    const canCreateChecklist = user?.role === "supervisor";
    // Comercial, Supervisor y Admin pueden asignar/modificar plan y subir documentación (diagnóstico + cotización + contrato).
    // El cliente lo VE en modo solo lectura.
    const canAssignPlan = ["comercial", "supervisor", "admin"].includes(user?.role) || !!user?.is_admin;
    // El equipo asignado (comercial/supervisor/técnicos) sólo lo edita supervisor o admin.
    const canEditTeam = user?.role === "supervisor" || user?.role === "admin" || !!user?.is_admin;

    const load = async () => {
        const [r, s] = await Promise.all([
            api.get(`/clients/${id}`),
            api.get(`/clients/${id}/subscription`).catch(() => ({ data: null })),
        ]);
        setC(r.data);
        setSub(s.data);
    };
    useEffect(() => { load(); }, [id]);

    if (!c) return <div className="p-8 text-zinc-500">Cargando…</div>;
    const visits = c.visits || [];
    const cerradas = visits.filter(v => v.estado === "cerrada").length;
    const rojas = visits.filter(v => v.inspection?.estado_salud === "rojo").length;

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-8" data-testid="client-detail-page">
            <Link to="/clients" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4" data-testid="back-clients">
                <ArrowLeft size={14} /> Volver a clientes
            </Link>

            <div className="border-b border-zinc-200 pb-6 mb-8">
                <div className="font-mono text-xs text-zinc-500 mb-1">{c.client_id} · NIT {c.nit}</div>
                <h1 className="font-display text-4xl font-black tracking-tighter">{c.razon_social}</h1>
                {c.sector && <div className="overline mt-2">{c.sector}</div>}
                <div className="flex flex-wrap gap-6 mt-4 text-sm text-zinc-700">
                    <span className="flex items-center gap-1.5"><MapPin size={14} /> {c.direccion}</span>
                    <span className="flex items-center gap-1.5"><Phone size={14} /> {c.telefono}</span>
                    <span className="flex items-center gap-1.5"><At size={14} /> {c.email}</span>
                </div>
                <LifecycleControl client={c} canEdit={user?.role === "comercial" || user?.role === "supervisor"} onChange={load} />
            </div>

            {/* Equipo asignado */}
            <div className="border border-zinc-200 p-6 mb-8" data-testid="equipo-card">
                <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
                    <div>
                        <div className="overline mb-1" style={{ color: "#0F766E" }}>Equipo asignado</div>
                        <h2 className="font-display text-xl font-bold tracking-tight">Contactos habituales de este cliente</h2>
                        <div className="text-xs text-zinc-500 mt-1">Referencia para agendar visitas — no restringe a quién se puede asignar.</div>
                    </div>
                    {canEditTeam && (
                        <button onClick={() => setShowTeamModal(true)} className="btn-secondary flex items-center gap-2" data-testid="edit-team-btn">
                            <Users size={16} /> {c.equipo_resuelto?.comercial || c.equipo_resuelto?.supervisor || c.equipo_resuelto?.tecnicos?.length ? "Editar equipo" : "Asignar equipo"}
                        </button>
                    )}
                </div>
                <div className="grid md:grid-cols-3 gap-0 border border-zinc-200">
                    <div className="p-4 border-r border-zinc-200 last:border-r-0">
                        <div className="overline mb-2">Comercial</div>
                        {c.equipo_resuelto?.comercial ? (
                            <div className="text-sm font-semibold">{c.equipo_resuelto.comercial.name}</div>
                        ) : <div className="text-sm text-zinc-400 italic">Sin asignar</div>}
                    </div>
                    <div className="p-4 border-r border-zinc-200 last:border-r-0">
                        <div className="overline mb-2">Supervisor / SISO / Arquitecto</div>
                        {c.equipo_resuelto?.supervisor ? (
                            <div className="text-sm font-semibold">{c.equipo_resuelto.supervisor.name}</div>
                        ) : <div className="text-sm text-zinc-400 italic">Sin asignar</div>}
                    </div>
                    <div className="p-4">
                        <div className="overline mb-2">Técnicos habituales</div>
                        {c.equipo_resuelto?.tecnicos?.length ? (
                            <div className="flex flex-col gap-1">
                                {c.equipo_resuelto.tecnicos.map(t => (
                                    <div key={t.cedula} className="text-sm">
                                        <span className="font-semibold">{t.nombre}</span>
                                        <span className="text-zinc-500 text-xs ml-1.5">· {t.especialidad || "General"}</span>
                                    </div>
                                ))}
                            </div>
                        ) : <div className="text-sm text-zinc-400 italic">Sin asignar</div>}
                    </div>
                </div>
            </div>

            {/* Documentos SST diarios (checklists, charlas, PTS, bitácoras, actos/condiciones) */}
            <DocumentosSST clientId={id} canCreate={canCreateChecklist} tipoProyectoSugerido={sub?.tipo_proyecto} />

            {/* Subscription card */}
            <div className="border border-zinc-200 p-6 mb-8" data-testid="subscription-card" style={{ background: sub ? "#fff" : "#FEF3C7" }}>
                <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="overline" style={{ color: "#0F766E" }}>Suscripción de mantenimiento</span>
                            {sub && (
                                <span className={`text-[10px] font-mono font-bold px-2 py-0.5 border ${sub.modalidad === "proyecto" ? "border-purple-500 bg-purple-50 text-purple-800" : sub.modalidad === "diseno" ? "border-blue-500 bg-blue-50 text-blue-800" : "border-teal-500 bg-teal-50 text-teal-800"}`} data-testid="modalidad-badge">
                                    {sub.modalidad === "proyecto" ? "PROYECTO PUNTUAL" : sub.modalidad === "diseno" ? "SERVICIO DE DISEÑO" : "PLAN MENSUAL"}
                                </span>
                            )}
                            {sub && (
                                <span className={`text-[10px] font-mono font-bold px-2 py-0.5 border ${sub.diagnostico_disponible ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-amber-400 bg-amber-50 text-amber-800"}`} data-testid="alcance-badge">
                                    {sub.diagnostico_disponible ? "✓ ALCANCE VERIFICADO" : "⚠ SIN RESPALDO"}
                                </span>
                            )}
                        </div>
                        {sub ? (
                            sub.modalidad === "proyecto" ? (
                                <>
                                    <h2 className="font-display text-2xl font-bold tracking-tight">{sub.nombre_proyecto || "Proyecto sin nombre"}</h2>
                                    <div className="text-sm text-zinc-600 mt-1 max-w-2xl">{sub.alcance_descripcion || <em className="text-zinc-400">Sin descripción de alcance</em>}</div>
                                    <div className="text-xs text-zinc-500 mt-1">Norma: <span className="font-semibold">{sub.norma_iso}</span> · Contrato: <span className="font-mono">{sub.numero_contrato || "—"}</span></div>
                                </>
                            ) : sub.modalidad === "diseno" ? (
                                <>
                                    <h2 className="font-display text-2xl font-bold tracking-tight">{sub.nombre_proyecto || "Servicio de diseño"}</h2>
                                    <div className="text-xs text-zinc-500 mt-1">{TIPOS_PROYECTO_LABELS[sub.tipo_proyecto] || "Tipo de proyecto sin definir"}</div>
                                </>
                            ) : (
                                <>
                                    <h2 className="font-display text-2xl font-bold tracking-tight">{sub.plan?.nombre} · {sub.plan?.descripcion?.slice(0, 60)}</h2>
                                    <div className="text-sm text-zinc-600 mt-1">Norma de referencia: <span className="font-semibold">{sub.norma_iso}</span></div>
                                </>
                            )
                        ) : (
                            <h2 className="font-display text-2xl font-bold tracking-tight" style={{ color: "#92400E" }}>Sin plan asignado</h2>
                        )}
                    </div>
                    {canAssignPlan && (
                        <button onClick={() => setShowPlanModal(true)} className="btn-accent flex items-center gap-2" data-testid="assign-plan-btn">
                            <Crown size={16} weight="fill" /> {sub ? "Editar suscripción" : "Asignar suscripción"}
                        </button>
                    )}
                </div>
                {sub && (
                    sub.modalidad === "proyecto" ? (
                        <>
                            {sub.cronograma && (
                                <div className="mt-2 mb-1 flex items-center gap-3 flex-wrap">
                                    <CronogramaBadge cronograma={sub.cronograma} showDetail />
                                    {sub.tiene_acta_inicio ? (
                                        <a href="#" onClick={async (e) => { e.preventDefault(); await downloadActaInicio(sub.subscription_id); }} className="text-xs font-semibold underline" style={{ color: "#0F766E" }} data-testid="download-acta-inicio">
                                            Ver Acta de Inicio →
                                        </a>
                                    ) : canEditTeam ? (
                                        <button onClick={() => setShowActaModal(true)} className="text-xs font-bold px-2 py-1 border border-amber-400 bg-amber-50 text-amber-800" data-testid="generar-acta-inicio-btn">
                                            ⚠ Generar Acta de Inicio
                                        </button>
                                    ) : (
                                        <span className="text-xs text-zinc-400 italic">Esperando Acta de Inicio</span>
                                    )}
                                </div>
                            )}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-zinc-200 mt-2">
                                <Stat label="Presupuesto total" value={COP(sub.presupuesto_total_cop)} mono />
                                <Stat label="Días restantes" value={sub.dias_restantes ?? "-"} highlight />
                                <Stat label="Visitas planeadas" value={sub.visitas_planeadas ?? 0} />
                                <Stat label="Visitas realizadas" value={sub.visitas_realizadas ?? 0} />
                            </div>
                        </>
                    ) : sub.modalidad === "diseno" ? (
                        <DisenoTracking sub={sub} canEdit={canAssignPlan} onChange={load} />
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-zinc-200 mt-2">
                            <Stat label="Plan" value={sub.plan?.nombre || "-"} />
                            <Stat label="Días restantes" value={sub.dias_restantes ?? "-"} highlight />
                            <Stat label="Visitas realizadas" value={sub.visitas_realizadas ?? 0} />
                            <Stat label="Valor mensual" value={COP(sub.precio_mensual)} mono />
                        </div>
                    )
                )}
                {sub && canAssignPlan && <DocsUploader sub={sub} onChange={load} />}
            </div>

            {sub && !!user?.is_admin && sub.subscription_id && <FinancieroPanel subscriptionId={sub.subscription_id} />}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-200 mb-8" data-testid="client-kpis">
                <Stat label="Visitas registradas" value={visits.length} />
                <Stat label="Cerradas" value={cerradas} />
                <Stat label="En curso / pendientes" value={visits.length - cerradas} />
                <Stat label="Hallazgos críticos" value={rojas} highlight />
            </div>

            <div className="overline mb-2">Historial de gestión</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mb-4">Todas las visitas</h2>
            <div className="border border-zinc-200 overflow-x-auto">
                <table className="atia-table">
                    <thead><tr><th>Orden</th><th>Sistema</th><th>Norma</th><th>Programada</th><th>Estado orden</th><th>Semáforo</th><th>Hallazgos</th></tr></thead>
                    <tbody>
                        {visits.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-zinc-500">Sin visitas todavía.</td></tr>}
                        {visits.map(v => (
                            <tr key={v.order_id} className="row-link" onClick={() => (window.location.href = v.inspection ? `/inspections/${v.inspection.inspection_id}` : `/orders/${v.order_id}`)}>
                                <td className="font-mono text-xs">{v.order_number}</td>
                                <td>{v.sistema_a_revisar}</td>
                                <td className="text-zinc-600 text-xs">{v.norma_iso}</td>
                                <td className="font-mono text-xs text-zinc-500">{v.fecha_programada?.slice(0, 10)}</td>
                                <td><EstadoOrden estado={v.estado} /></td>
                                <td>{v.inspection ? <TrafficBadge value={v.inspection.estado_salud} /> : <span className="text-zinc-400 text-xs">—</span>}</td>
                                <td className="font-mono text-xs">{v.inspection?.hallazgos_count ?? "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showPlanModal && <AssignPlanModal clientId={id} currentSub={sub} onClose={() => setShowPlanModal(false)} onAssigned={() => { setShowPlanModal(false); load(); }} />}
            {showTeamModal && <TeamModal clientId={id} currentEquipo={c.equipo_asignado} onClose={() => setShowTeamModal(false)} onSaved={() => { setShowTeamModal(false); load(); }} />}
            {showActaModal && sub && <ActaInicioModal subscriptionId={sub.subscription_id} alcanceDefault={sub.alcance_descripcion} onClose={() => setShowActaModal(false)} onSaved={() => { setShowActaModal(false); load(); }} />}
        </div>
    );
}

function Stat({ label, value, highlight, mono }) {
    return (
        <div className={`p-5 border-r border-zinc-200 last:border-r-0 ${highlight ? "bg-zinc-50" : ""}`}>
            <div className="overline mb-2">{label}</div>
            <div className={`font-display text-2xl font-black tracking-tighter ${mono ? "font-mono" : ""}`}>{value}</div>
        </div>
    );
}

function AssignPlanModal({ clientId, currentSub, onClose, onAssigned }) {
    const [plans, setPlans] = useState([]);
    const [modalidad, setModalidad] = useState(currentSub?.modalidad || "mensual");
    // Mensual
    const [selected, setSelected] = useState(currentSub?.plan_id || "");
    const [duracion, setDuracion] = useState(currentSub?.duracion_meses || 12);
    const [norma, setNorma] = useState(currentSub?.norma_iso || "ISO 9001:2015");
    // Proyecto
    const [nombreProyecto, setNombreProyecto] = useState(currentSub?.nombre_proyecto || "");
    const [tipoProyecto, setTipoProyecto] = useState(currentSub?.tipo_proyecto || "");
    const [tiposProyecto, setTiposProyecto] = useState({});
    const [alcance, setAlcance] = useState(currentSub?.alcance_descripcion || "");
    const [presupuesto, setPresupuesto] = useState(currentSub?.presupuesto_total_cop || 0);
    const [visitas, setVisitas] = useState(currentSub?.visitas_planeadas || 3);
    const [fechaFin, setFechaFin] = useState(currentSub?.fecha_fin_estimada?.slice(0, 10) || "");
    const [numeroContrato, setNumeroContrato] = useState(currentSub?.numero_contrato || "");
    const [repNombre, setRepNombre] = useState(currentSub?.representante_legal_nombre || "");
    const [repCedula, setRepCedula] = useState(currentSub?.representante_legal_cedula || "");
    const [repCargo, setRepCargo] = useState(currentSub?.representante_legal_cargo || "");
    const [hitos, setHitos] = useState(currentSub?.hitos?.length ? currentSub.hitos : [
        { nombre: "Anticipo — Firma de contrato", porcentaje: 30, estado: "programado" },
        { nombre: "Avance 50% — Ejecución técnica", porcentaje: 40, estado: "programado" },
        { nombre: "Cierre y entrega final", porcentaje: 30, estado: "programado" },
    ]);
    const [fechaFinDiseno, setFechaFinDiseno] = useState(currentSub?.fecha_fin_estimada?.slice(0, 10) || "");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => { (async () => setPlans((await api.get("/plans-catalog")).data))(); }, []);
    useEffect(() => { (async () => setTiposProyecto((await api.get("/tipos-proyecto-construccion")).data))(); }, []);

    const totalPct = hitos.reduce((a, h) => a + (parseInt(h.porcentaje) || 0), 0);
    const canSubmitMensual = !!selected;
    const canSubmitProyecto = !!nombreProyecto && !!alcance && presupuesto > 0 && visitas > 0 && !!fechaFin && totalPct === 100;
    const canSubmitDiseno = !!nombreProyecto;
    const canSubmit = modalidad === "mensual" ? canSubmitMensual : modalidad === "diseno" ? canSubmitDiseno : canSubmitProyecto;

    const updateHito = (idx, key, val) => setHitos(h => h.map((x, i) => i === idx ? { ...x, [key]: val } : x));
    const addHito = () => setHitos(h => [...h, { nombre: "Nuevo hito", porcentaje: 0, estado: "programado" }]);
    const removeHito = (idx) => setHitos(h => h.filter((_, i) => i !== idx));

    const isEditMode = !!currentSub;

    const submit = async () => {
        setLoading(true); setError("");
        try {
            if (modalidad === "mensual") {
                const payload = { modalidad: "mensual", plan_id: selected, duracion_meses: duracion, norma_iso: norma };
                if (isEditMode && currentSub.modalidad === "mensual") {
                    await api.put(`/subscriptions/${currentSub.subscription_id}`, payload);
                } else {
                    await api.post("/subscriptions", { client_id: clientId, ...payload });
                }
            } else if (modalidad === "diseno") {
                const payload = {
                    modalidad: "diseno",
                    nombre_proyecto: nombreProyecto,
                    tipo_proyecto: tipoProyecto || null,
                    presupuesto_total_cop: presupuesto || null,
                    fecha_fin_estimada: fechaFinDiseno ? new Date(fechaFinDiseno).toISOString() : null,
                };
                if (isEditMode && currentSub.modalidad === "diseno") {
                    await api.put(`/subscriptions/${currentSub.subscription_id}`, payload);
                } else {
                    await api.post("/subscriptions", { client_id: clientId, ...payload });
                }
            } else {
                const payload = {
                    modalidad: "proyecto",
                    nombre_proyecto: nombreProyecto,
                    tipo_proyecto: tipoProyecto || null,
                    alcance_descripcion: alcance,
                    presupuesto_total_cop: presupuesto,
                    visitas_planeadas: visitas,
                    fecha_fin_estimada: new Date(fechaFin).toISOString(),
                    numero_contrato: numeroContrato || null,
                    representante_legal_nombre: repNombre || null,
                    representante_legal_cedula: repCedula || null,
                    representante_legal_cargo: repCargo || null,
                    hitos: hitos.map(h => ({ nombre: h.nombre, porcentaje: parseInt(h.porcentaje) || 0, estado: h.estado || "programado" })),
                    norma_iso: norma,
                };
                if (isEditMode && currentSub.modalidad === "proyecto") {
                    await api.put(`/subscriptions/${currentSub.subscription_id}`, payload);
                } else {
                    await api.post("/subscriptions", { client_id: clientId, ...payload });
                }
            }
            onAssigned();
        } catch (e) { setError(e.response?.data?.detail || "Error"); setLoading(false); }
    };

    // Cierre por click en backdrop: sólo cerrar si mousedown Y mouseup ocurrieron en el backdrop
    // (evita cerrar por drag durante selección de texto en inputs)
    const backdrop = useBackdropClose(onClose);

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="assign-plan-modal" {...backdrop.backdropProps}>
            <div className="bg-white max-w-4xl w-full max-h-[92vh] overflow-y-auto" {...backdrop.contentProps}>
                <div className="p-6 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="overline mb-1" style={{ color: "#0F766E" }}>{isEditMode ? "Editando" : "Nuevo"} · Modelo de contrato</div>
                        <h2 className="font-display text-3xl font-black tracking-tighter">
                            {modalidad === "mensual" ? "Plan mensual recurrente" : modalidad === "diseno" ? "Servicio de diseño" : "Proyecto puntual"}
                        </h2>
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-3xl leading-none" data-testid="close-modal">×</button>
                </div>

                {/* Selector modalidad */}
                <div className="p-6 border-b border-zinc-200">
                    <div className="overline mb-3">Escoge el tipo de contrato</div>
                    <div className="grid md:grid-cols-3 gap-3">
                        <button onClick={() => setModalidad("mensual")} className={`text-left p-5 border transition-all ${modalidad === "mensual" ? "border-teal-500 bg-teal-50" : "border-zinc-200 hover:border-zinc-400"}`} data-testid="modalidad-mensual">
                            <div className="flex items-center justify-between mb-1">
                                <div className="font-display text-xl font-black tracking-tighter">MENSUAL</div>
                                {modalidad === "mensual" && <Check size={18} weight="bold" style={{ color: "#14B8A6" }} />}
                            </div>
                            <div className="text-xs text-zinc-600">Plan recurrente (Basic/Pro/Elite). Facturación mensual, visitas periódicas continuas.</div>
                        </button>
                        <button onClick={() => setModalidad("diseno")} className={`text-left p-5 border transition-all ${modalidad === "diseno" ? "border-teal-500 bg-teal-50" : "border-zinc-200 hover:border-zinc-400"}`} data-testid="modalidad-diseno">
                            <div className="flex items-center justify-between mb-1">
                                <div className="font-display text-xl font-black tracking-tighter">DISEÑO</div>
                                {modalidad === "diseno" && <Check size={18} weight="bold" style={{ color: "#14B8A6" }} />}
                            </div>
                            <div className="text-xs text-zinc-600">Topografía, estudios, diseños técnicos y trámite de licencia. Punto de partida antes de la obra.</div>
                        </button>
                        <button onClick={() => setModalidad("proyecto")} className={`text-left p-5 border transition-all ${modalidad === "proyecto" ? "border-teal-500 bg-teal-50" : "border-zinc-200 hover:border-zinc-400"}`} data-testid="modalidad-proyecto">
                            <div className="flex items-center justify-between mb-1">
                                <div className="font-display text-xl font-black tracking-tighter">PROYECTO</div>
                                {modalidad === "proyecto" && <Check size={18} weight="bold" style={{ color: "#14B8A6" }} />}
                            </div>
                            <div className="text-xs text-zinc-600">Alcance específico y duración fija. Presupuesto total, hitos de facturación, entregable definido.</div>
                        </button>
                    </div>
                    {isEditMode && currentSub.modalidad !== modalidad && (
                        <div className="mt-3 text-xs font-mono px-3 py-2 border border-amber-400 bg-amber-50 text-amber-800" data-testid="modalidad-change-warning">
                            ⚠ Cambiaste el tipo de contrato — esto creará una suscripción NUEVA en vez de editar la actual. Los documentos ya cargados (diagnóstico, cotización, contrato) no se copiarán automáticamente.
                        </div>
                    )}
                </div>

                {/* Formulario según modalidad */}
                {modalidad === "mensual" ? (
                    <>
                        <div className="p-6 grid md:grid-cols-3 gap-3">
                            {plans.map(p => (
                                <button key={p.plan_id} onClick={() => setSelected(p.plan_id)} className={`text-left p-5 border transition-all ${selected === p.plan_id ? "border-teal-500 bg-teal-50" : "border-zinc-200 hover:border-zinc-400"}`} data-testid={`plan-card-${p.plan_id}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="overline" style={{ color: "#0F766E" }}>{p.tier}</div>
                                        {selected === p.plan_id && <Check size={18} weight="bold" style={{ color: "#14B8A6" }} />}
                                    </div>
                                    <div className="font-display text-2xl font-black tracking-tighter">{p.nombre}</div>
                                    <div className="font-mono text-lg font-bold mt-1" style={{ color: "#0F766E" }}>{COP(p.precio_mensual)}<span className="text-xs text-zinc-500 font-normal">/mes</span></div>
                                    <div className="text-xs text-zinc-600 mt-3 leading-relaxed">{p.descripcion}</div>
                                    <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-zinc-100 text-xs">
                                        <div><div className="text-zinc-500">Frecuencia</div><div className="font-bold">{p.frecuencia_dias}d</div></div>
                                        <div><div className="text-zinc-500">Sedes</div><div className="font-bold">{p.max_sedes}</div></div>
                                    </div>
                                </button>
                            ))}
                        </div>
                        <div className="px-6 grid md:grid-cols-2 gap-4 pb-4">
                            <label>
                                <div className="overline mb-1.5">Duración (meses)</div>
                                <input type="number" min={1} max={36} className="input-atia font-mono" value={duracion} onChange={e => setDuracion(parseInt(e.target.value) || 12)} data-testid="duration-input" />
                            </label>
                            <label>
                                <div className="overline mb-1.5">Norma ISO de referencia</div>
                                <select className="input-atia" value={norma} onChange={e => setNorma(e.target.value)} data-testid="norma-select">
                                    {["ISO 9001:2015", "ISO 17020:2012", "ISO 45001:2018", "ISO 14001:2015"].map(n => <option key={n}>{n}</option>)}
                                </select>
                            </label>
                        </div>
                    </>
                ) : modalidad === "diseno" ? (
                    <div className="p-6 space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                            <label className="md:col-span-2">
                                <div className="overline mb-1.5">Nombre del servicio *</div>
                                <input type="text" className="input-atia" value={nombreProyecto} onChange={e => setNombreProyecto(e.target.value)} placeholder="Ej: Diseño Casa Principal" data-testid="diseno-nombre" />
                            </label>
                            <label className="md:col-span-2">
                                <div className="overline mb-1.5">Tipo de proyecto (define qué checklist usará el supervisor después)</div>
                                <select className="input-atia" value={tipoProyecto} onChange={e => setTipoProyecto(e.target.value)} data-testid="diseno-tipo">
                                    <option value="">— Sin definir —</option>
                                    {Object.entries(tiposProyecto).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                                </select>
                            </label>
                            <label>
                                <div className="overline mb-1.5">Presupuesto (opcional)</div>
                                <input type="number" className="input-atia font-mono" value={presupuesto} onChange={e => setPresupuesto(parseInt(e.target.value) || 0)} placeholder="COP" data-testid="diseno-presupuesto" />
                            </label>
                            <label>
                                <div className="overline mb-1.5">Fecha estimada de fin</div>
                                <input type="date" className="input-atia" value={fechaFinDiseno} onChange={e => setFechaFinDiseno(e.target.value)} data-testid="diseno-fecha-fin" />
                            </label>
                        </div>
                        <div className="text-xs text-zinc-500 border border-zinc-200 p-3">
                            Al contratar este servicio podrás asignar un técnico coordinador de diseño y hacer seguimiento a cada entregable (topografía, estudio de suelos, diseño estructural, etc.) y a las observaciones de curaduría — todo desde la ficha de esta suscripción.
                        </div>
                    </div>
                ) : (
                    <div className="p-6 space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                            <label className="md:col-span-2">
                                <div className="overline mb-1.5">Nombre del proyecto *</div>
                                <input type="text" className="input-atia" value={nombreProyecto} onChange={e => setNombreProyecto(e.target.value)} placeholder="Ej: Mantenimiento fachadas Torre A" data-testid="proyecto-nombre" />
                            </label>
                            <label className="md:col-span-2">
                                <div className="overline mb-1.5">Tipo de proyecto (define qué checklist usará el supervisor)</div>
                                <select className="input-atia" value={tipoProyecto} onChange={e => setTipoProyecto(e.target.value)} data-testid="proyecto-tipo">
                                    <option value="">— Sin definir —</option>
                                    {Object.entries(tiposProyecto).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                                </select>
                            </label>
                            <label className="md:col-span-2">
                                <div className="overline mb-1.5">Alcance del contrato *</div>
                                <textarea rows={3} className="input-atia" value={alcance} onChange={e => setAlcance(e.target.value)} placeholder="Descripción concreta del alcance (será visible al cliente en su portal)" data-testid="proyecto-alcance" />
                                <div className="text-[11px] text-amber-700 mt-1">⚠️ Este texto se muestra al cliente. Debe coincidir con el diagnóstico técnico que subirás abajo.</div>
                            </label>
                            <label>
                                <div className="overline mb-1.5">Presupuesto total (COP) *</div>
                                <input type="number" inputMode="numeric" min={0} step={100000} className="input-atia font-mono" value={presupuesto} onChange={e => setPresupuesto(parseInt(e.target.value) || 0)} onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }} data-testid="proyecto-presupuesto" />
                                <div className="text-[11px] text-zinc-500 mt-1 font-mono">{COP(presupuesto)}</div>
                            </label>
                            <label>
                                <div className="overline mb-1.5">Visitas planeadas *</div>
                                <input type="number" inputMode="numeric" min={1} className="input-atia font-mono" value={visitas} onChange={e => setVisitas(parseInt(e.target.value) || 1)} onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }} data-testid="proyecto-visitas" />
                            </label>
                            <label>
                                <div className="overline mb-1.5">Fecha estimada de cierre *</div>
                                <input type="date" className="input-atia" value={fechaFin} onChange={e => setFechaFin(e.target.value)} data-testid="proyecto-fecha-fin" />
                            </label>
                            <label>
                                <div className="overline mb-1.5">Norma ISO de referencia</div>
                                <select className="input-atia" value={norma} onChange={e => setNorma(e.target.value)} data-testid="proyecto-norma">
                                    {["ISO 9001:2015", "ISO 17020:2012", "ISO 45001:2018", "ISO 14001:2015"].map(n => <option key={n}>{n}</option>)}
                                </select>
                            </label>
                            <label>
                                <div className="overline mb-1.5">Número de contrato</div>
                                <input type="text" className="input-atia font-mono" value={numeroContrato} onChange={e => setNumeroContrato(e.target.value)} placeholder="ATIA-PH-2026-001" data-testid="proyecto-num-contrato" />
                            </label>
                            <label>
                                <div className="overline mb-1.5">Representante legal · Nombre</div>
                                <input type="text" className="input-atia" value={repNombre} onChange={e => setRepNombre(e.target.value)} data-testid="proyecto-rep-nombre" />
                            </label>
                            <label>
                                <div className="overline mb-1.5">Representante legal · Cédula</div>
                                <input type="text" className="input-atia font-mono" value={repCedula} onChange={e => setRepCedula(e.target.value)} data-testid="proyecto-rep-cedula" />
                            </label>
                            <label className="md:col-span-2">
                                <div className="overline mb-1.5">Representante legal · Cargo</div>
                                <input type="text" className="input-atia" value={repCargo} onChange={e => setRepCargo(e.target.value)} placeholder="Presidente PH, Gerente General, etc." data-testid="proyecto-rep-cargo" />
                            </label>
                        </div>

                        {/* Hitos editor */}
                        <div className="border border-zinc-200 p-4 bg-zinc-50">
                            <div className="flex items-center justify-between mb-3">
                                <div className="overline" style={{ color: "#0F766E" }}>Cronograma de hitos de facturación</div>
                                <span className={`text-[11px] font-mono font-bold px-2 py-1 border ${totalPct === 100 ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-amber-400 bg-amber-50 text-amber-800"}`} data-testid="hitos-total-pct">
                                    Suma: {totalPct}% {totalPct === 100 ? "✓" : "(debe ser 100)"}
                                </span>
                            </div>
                            <div className="space-y-2">
                                {hitos.map((h, i) => (
                                    <div key={i} className="grid grid-cols-[1fr_100px_120px_30px] gap-2 items-center" data-testid={`hito-row-${i}`}>
                                        <input type="text" className="input-atia text-sm" value={h.nombre} onChange={e => updateHito(i, "nombre", e.target.value)} onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }} data-testid={`hito-nombre-${i}`} />
                                        <input type="number" inputMode="numeric" min={0} max={100} className="input-atia font-mono text-sm" value={h.porcentaje} onChange={e => updateHito(i, "porcentaje", e.target.value)} onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }} data-testid={`hito-pct-${i}`} />
                                        <div className="text-xs font-mono text-zinc-600">{COP(presupuesto * (parseInt(h.porcentaje) || 0) / 100)}</div>
                                        <button type="button" onClick={() => removeHito(i)} className="text-red-500 hover:text-red-700 text-xl" data-testid={`hito-remove-${i}`} title="Eliminar">×</button>
                                    </div>
                                ))}
                            </div>
                            <button type="button" onClick={addHito} className="btn-secondary text-xs mt-3" data-testid="hito-add">+ Agregar hito</button>
                        </div>
                    </div>
                )}

                {error && <div className="mx-6 mb-4 p-3 text-sm border border-red-300 bg-red-50 text-red-900" data-testid="assign-error">{error}</div>}

                <div className="p-6 border-t border-zinc-200 flex justify-end gap-3 sticky bottom-0 bg-white">
                    <button type="button" onClick={onClose} className="btn-secondary" data-testid="cancel-assign">Cancelar</button>
                    <button type="button" onClick={submit} disabled={!canSubmit || loading} className="btn-accent" data-testid="confirm-assign">
                        {loading ? "Guardando…" : currentSub ? "Actualizar suscripción →" : "Crear suscripción →"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const LIFECYCLE_STAGES = [
    { code: "prospecto",     label: "Prospecto",      color: "#71717A", desc: "Cliente registrado, sin cotización" },
    { code: "cotizado",      label: "Cotizado",       color: "#F59E0B", desc: "Cotización enviada, esperando aprobación" },
    { code: "contratado",    label: "Contratado",     color: "#0EA5E9", desc: "Contrato firmado, listo para iniciar" },
    { code: "en_ejecucion",  label: "En ejecución",   color: "#14B8A6", desc: "Proyecto activo con visitas en curso" },
    { code: "cerrado",       label: "Cerrado",        color: "#10B981", desc: "Proyecto finalizado" },
];

function LifecycleControl({ client, canEdit, onChange }) {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const current = client.estado_lifecycle || "prospecto";
    const stage = LIFECYCLE_STAGES.find(s => s.code === current) || LIFECYCLE_STAGES[0];
    const history = client.lifecycle_history || [];

    const changeTo = async (next) => {
        const obs = window.prompt(`Observación para cambiar a "${next}" (opcional):`) || "";
        setSaving(true); setError("");
        try {
            await api.patch(`/clients/${client.client_id}/lifecycle`, { estado_lifecycle: next, observacion: obs });
            onChange();
        } catch (e) { setError(e.response?.data?.detail || "Error al cambiar estado"); }
        setSaving(false);
    };

    return (
        <div className="mt-4 p-4 border border-zinc-200 bg-zinc-50" data-testid="lifecycle-control">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <div className="overline">Estado del ciclo comercial</div>
                    <span className="badge-sev" style={{ background: stage.color + "20", color: stage.color, borderColor: stage.color, borderWidth: 1, borderStyle: "solid" }} data-testid="lifecycle-current">
                        {stage.label.toUpperCase()}
                    </span>
                    <span className="text-xs text-zinc-500">{stage.desc}</span>
                </div>
                {canEdit && (
                    <select
                        className="input-atia text-xs w-auto"
                        value={current}
                        disabled={saving}
                        onChange={e => changeTo(e.target.value)}
                        data-testid="lifecycle-select">
                        {LIFECYCLE_STAGES.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
                    </select>
                )}
            </div>
            {error && <div className="mt-2 p-2 text-xs border border-red-300 bg-red-50 text-red-900" data-testid="lifecycle-error">{error}</div>}
            {history.length > 0 && (
                <details className="mt-3">
                    <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-800" data-testid="lifecycle-history-toggle">Ver histórico ({history.length})</summary>
                    <ul className="mt-2 space-y-1 text-xs">
                        {history.slice().reverse().map((h, i) => (
                            <li key={i} className="flex items-start gap-2 text-zinc-600">
                                <span className="font-mono text-zinc-400">{h.fecha?.slice(0, 10)}</span>
                                <span className="font-semibold">→ {h.estado}</span>
                                {h.observacion && <span className="italic">&ldquo;{h.observacion}&rdquo;</span>}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
}


const fileToB64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(",")[1]);
    r.onerror = rej;
    r.readAsDataURL(file);
});

function DocsUploader({ sub, onChange }) {
    const [uploadingKind, setUploadingKind] = useState(null);
    const [error, setError] = useState("");

    const upload = async (kind, file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { setError("Archivo demasiado grande (máx 5MB)"); return; }
        setUploadingKind(kind); setError("");
        try {
            const b64 = await fileToB64(file);
            await api.post(`/subscriptions/${sub.subscription_id}/${kind}`, {
                filename: file.name, file_base64: b64,
            });
            onChange();
        } catch (e) {
            setError(e.response?.data?.detail || "Error al subir");
        }
        setUploadingKind(null);
    };

    const download = async (kind, defaultName) => {
        try {
            const r = await api.get(`/subscriptions/${sub.subscription_id}/${kind}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = defaultName; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { alert(e.response?.data?.detail || "Sin archivo"); }
    };

    const hasDiagnostic = !!sub.diagnostico_pdf_filename || !!sub.diagnostico_disponible;
    const hasQuote = !!sub.cotizacion_pdf_filename || !!sub.cotizacion_disponible;
    const hasContract = !!sub.contrato_pdf_filename || !!sub.contrato_disponible;
    const completed = [hasDiagnostic, hasQuote, hasContract].filter(Boolean).length;
    const allDone = completed === 3;

    return (
        <div className="border border-zinc-200 mt-4 p-4 bg-zinc-50" data-testid="docs-uploader">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="overline" style={{ color: "#0F766E" }}>Documentos comerciales · Respaldo del alcance</div>
                <span className={`text-[11px] font-mono font-bold px-2 py-1 border ${allDone ? "border-emerald-500 bg-emerald-50 text-emerald-800" : completed > 0 ? "border-amber-400 bg-amber-50 text-amber-800" : "border-zinc-300 bg-white text-zinc-500"}`} data-testid="docs-progress">
                    {completed}/3 CARGADOS
                </span>
            </div>
            {allDone && (
                <div className="mb-3 p-2 border border-emerald-300 bg-emerald-50 text-[11px] text-emerald-900 flex items-center gap-2" data-testid="docs-complete-banner">
                    <Check size={14} weight="bold" /> Documentación completa · el cliente puede validar el alcance real del contrato
                </div>
            )}
            <div className="grid md:grid-cols-3 gap-3">
                <DocRow
                    title="1 · Diagnóstico técnico"
                    hint="PDF con el diagnóstico que respalda el alcance ofrecido"
                    filename={sub.diagnostico_pdf_filename}
                    hasFile={hasDiagnostic}
                    kind="diagnostic"
                    onUpload={upload}
                    onDownload={() => download("diagnostic", "diagnostico_tecnico.pdf")}
                    uploading={uploadingKind === "diagnostic"}
                />
                <DocRow
                    title="2 · Cotización aprobada"
                    hint="PDF de la cotización enviada al cliente para aprobación"
                    filename={sub.cotizacion_pdf_filename}
                    hasFile={hasQuote}
                    kind="quote"
                    onUpload={upload}
                    onDownload={() => download("quote", "cotizacion.pdf")}
                    uploading={uploadingKind === "quote"}
                />
                <DocRow
                    title="3 · Contrato firmado"
                    hint="PDF del contrato firmado por representante legal"
                    filename={sub.contrato_pdf_filename}
                    hasFile={hasContract}
                    kind="contract"
                    onUpload={upload}
                    onDownload={() => download("contract", "contrato_firmado.pdf")}
                    uploading={uploadingKind === "contract"}
                />
            </div>
            {error && <div className="mt-3 p-2 text-xs border border-red-300 bg-red-50 text-red-900" data-testid="docs-error">{error}</div>}
        </div>
    );
}

function DocRow({ title, hint, filename, hasFile, kind, onUpload, onDownload, uploading }) {
    const inputId = `doc-${kind}-input`;
    return (
        <div className="border border-zinc-200 bg-white p-3" data-testid={`doc-row-${kind}`}>
            <div className="flex items-center justify-between mb-2">
                <div>
                    <div className="font-semibold text-sm">{title}</div>
                    <div className="text-[11px] text-zinc-500">{hint}</div>
                </div>
                <span className={`badge-sev ${hasFile ? "sev-verde" : "sev-media"}`}>{hasFile ? "CARGADO" : "PENDIENTE"}</span>
            </div>
            {filename && (
                <div className="text-xs font-mono text-zinc-600 mb-2 truncate">📄 {filename}</div>
            )}
            <div className="flex gap-2">
                <input type="file" accept="application/pdf" id={inputId} className="hidden" onChange={e => onUpload(kind, e.target.files?.[0])} data-testid={`doc-file-${kind}`} />
                <label htmlFor={inputId} className="btn-secondary text-xs flex items-center gap-1 cursor-pointer" data-testid={`doc-upload-${kind}`}>
                    {uploading ? "Subiendo…" : (hasFile ? "Reemplazar" : "Subir PDF")}
                </label>
                {hasFile && (
                    <button onClick={onDownload} className="btn-secondary text-xs" data-testid={`doc-download-${kind}`}>Descargar</button>
                )}
            </div>
        </div>
    );
}

function TeamModal({ clientId, currentEquipo, onClose, onSaved }) {
    const [comerciales, setComerciales] = useState([]);
    const [supervisores, setSupervisores] = useState([]);
    const [tecnicos, setTecnicos] = useState([]);
    const [comercialCedula, setComercialCedula] = useState(currentEquipo?.comercial_cedula || "");
    const [supervisorCedula, setSupervisorCedula] = useState(currentEquipo?.supervisor_cedula || "");
    const [tecnicosCedulas, setTecnicosCedulas] = useState(currentEquipo?.tecnicos_cedulas || []);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const [rc, rs, rt] = await Promise.all([
                    api.get("/admin/users", { params: { role: "comercial" } }),
                    api.get("/admin/users", { params: { role: "supervisor" } }),
                    api.get("/technicians"),
                ]);
                setComerciales(rc.data || []);
                setSupervisores(rs.data || []);
                setTecnicos(rt.data || []);
            } catch (e) { /* si no es admin, listas quedan vacías y se puede escribir cédula manualmente en el futuro */ }
        })();
    }, []);

    const toggleTecnico = (cedula) => {
        setTecnicosCedulas(prev => prev.includes(cedula) ? prev.filter(c => c !== cedula) : [...prev, cedula]);
    };

    const submit = async () => {
        setLoading(true); setError("");
        try {
            await api.put(`/clients/${clientId}/equipo`, {
                comercial_cedula: comercialCedula || null,
                supervisor_cedula: supervisorCedula || null,
                tecnicos_cedulas: tecnicosCedulas,
            });
            onSaved();
        } catch (e) { setError(e.response?.data?.detail || "Error"); setLoading(false); }
    };

    const backdrop = useBackdropClose(onClose);
    // Agrupar técnicos por especialidad para que sea fácil escoger "varios posibles por especialidad"
    const tecnicosPorEspecialidad = tecnicos.reduce((acc, t) => {
        const esp = t.especialidad || "General";
        acc[esp] = acc[esp] || [];
        acc[esp].push(t);
        return acc;
    }, {});

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="team-modal" {...backdrop.backdropProps}>
            <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" {...backdrop.contentProps}>
                <div className="p-6 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="overline mb-1" style={{ color: "#0F766E" }}>Equipo asignado</div>
                        <h2 className="font-display text-2xl font-black tracking-tighter">Contactos habituales del cliente</h2>
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-3xl leading-none" data-testid="close-team-modal">×</button>
                </div>

                <div className="p-6 space-y-6">
                    <div>
                        <label className="overline block mb-2">Comercial</label>
                        <select value={comercialCedula} onChange={e => setComercialCedula(e.target.value)} className="input-atia w-full" data-testid="select-comercial">
                            <option value="">— Sin asignar —</option>
                            {comerciales.map(u => <option key={u.cedula} value={u.cedula}>{u.name} · CC {u.cedula}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="overline block mb-2">Supervisor / SISO / Arquitecto acompañante</label>
                        <select value={supervisorCedula} onChange={e => setSupervisorCedula(e.target.value)} className="input-atia w-full" data-testid="select-supervisor">
                            <option value="">— Sin asignar —</option>
                            {supervisores.map(u => <option key={u.cedula} value={u.cedula}>{u.name} · CC {u.cedula}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="overline block mb-2">Técnicos habituales (puedes elegir varios por especialidad)</label>
                        <div className="border border-zinc-200 max-h-64 overflow-y-auto">
                            {Object.keys(tecnicosPorEspecialidad).length === 0 && (
                                <div className="p-4 text-sm text-zinc-400 italic">No hay técnicos registrados.</div>
                            )}
                            {Object.entries(tecnicosPorEspecialidad).map(([esp, lista]) => (
                                <div key={esp} className="border-b border-zinc-100 last:border-b-0">
                                    <div className="px-3 py-1.5 bg-zinc-50 text-[10px] font-mono font-bold uppercase tracking-wide text-zinc-500">{esp}</div>
                                    {lista.map(t => (
                                        <label key={t.cedula} className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 cursor-pointer text-sm" data-testid={`tecnico-option-${t.cedula}`}>
                                            <input type="checkbox" checked={tecnicosCedulas.includes(t.cedula)} onChange={() => toggleTecnico(t.cedula)} />
                                            {t.nombre} <span className="text-zinc-400 text-xs font-mono">· CC {t.cedula}</span>
                                        </label>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    {error && <div className="text-xs text-red-600 font-mono">{error}</div>}
                </div>

                <div className="p-6 border-t border-zinc-200 flex justify-end gap-3 sticky bottom-0 bg-white">
                    <button type="button" onClick={onClose} className="btn-secondary" data-testid="cancel-team">Cancelar</button>
                    <button type="button" onClick={submit} disabled={loading} className="btn-accent" data-testid="confirm-team">
                        {loading ? "Guardando…" : "Guardar equipo →"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function FinancieroPanel({ subscriptionId }) {
    const [fin, setFin] = useState(null);
    const [tecnicos, setTecnicos] = useState([]);
    const [comerciales, setComerciales] = useState([]);
    const [error, setError] = useState("");
    // formularios
    const [gConcepto, setGConcepto] = useState("");
    const [gCategoria, setGCategoria] = useState("materiales");
    const [gMonto, setGMonto] = useState("");
    const [ctTecnico, setCtTecnico] = useState("");
    const [ctValor, setCtValor] = useState("");
    const [pagoTecnico, setPagoTecnico] = useState("");
    const [pagoMonto, setPagoMonto] = useState("");
    const [pagoConcepto, setPagoConcepto] = useState("");
    const [comComercial, setComComercial] = useState("");
    const [comPorcentaje, setComPorcentaje] = useState("");

    const load = async () => {
        try {
            const r = await api.get(`/subscriptions/${subscriptionId}/financiero`);
            setFin(r.data);
        } catch (e) { setError(e.response?.data?.detail || "No se pudo cargar el módulo financiero"); }
    };
    useEffect(() => {
        load();
        (async () => {
            try {
                const [rt, rc] = await Promise.all([
                    api.get("/technicians"),
                    api.get("/admin/users", { params: { role: "comercial" } }),
                ]);
                setTecnicos(rt.data || []);
                setComerciales(rc.data || []);
            } catch (e) { /* listas quedan vacías si no hay permiso admin pleno */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscriptionId]);

    if (error) return (
        <div className="border border-red-300 bg-red-50 p-4 mb-8 text-sm text-red-700 font-mono" data-testid="financiero-error">{error}</div>
    );
    if (!fin) return <div className="mb-8 text-sm text-zinc-400">Cargando módulo financiero…</div>;

    const addGasto = async () => {
        if (!gConcepto || !gMonto) return;
        await api.post(`/subscriptions/${subscriptionId}/gastos`, { concepto: gConcepto, categoria: gCategoria, monto_cop: parseInt(gMonto) });
        setGConcepto(""); setGMonto(""); load();
    };
    const delGasto = async (id) => { await api.delete(`/subscriptions/${subscriptionId}/gastos/${id}`); load(); };

    const setContrato = async () => {
        if (!ctTecnico || ctValor === "") return;
        await api.put(`/subscriptions/${subscriptionId}/contrato-tecnico`, { tecnico_cedula: ctTecnico, valor_contratado_cop: parseInt(ctValor) });
        setCtTecnico(""); setCtValor(""); load();
    };
    const delContrato = async (cedula) => { await api.delete(`/subscriptions/${subscriptionId}/contrato-tecnico/${cedula}`); load(); };
    const addPago = async () => {
        if (!pagoTecnico || !pagoMonto) return;
        await api.post(`/subscriptions/${subscriptionId}/contrato-tecnico/${pagoTecnico}/pagos`, { monto_cop: parseInt(pagoMonto), concepto: pagoConcepto || "Pago parcial" });
        setPagoTecnico(""); setPagoMonto(""); setPagoConcepto(""); load();
    };

    const saveComision = async () => {
        if (!comComercial || comPorcentaje === "") return;
        await api.put(`/subscriptions/${subscriptionId}/comision`, { comercial_cedula: comComercial, porcentaje: parseFloat(comPorcentaje) });
        setComComercial(""); setComPorcentaje(""); load();
    };

    const pct = (n) => `${n}%`;

    return (
        <div className="border-2 border-zinc-900 p-6 mb-8" data-testid="financiero-panel">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                    <div className="overline mb-1" style={{ color: "#0F766E" }}>🔒 Solo administradores</div>
                    <h2 className="font-display text-2xl font-bold tracking-tight">Financiero del proyecto</h2>
                </div>
                <a href={`${api.defaults.baseURL}/subscriptions/${subscriptionId}/financiero/pdf`} target="_blank" rel="noopener noreferrer"
                   onClick={async (e) => {
                       e.preventDefault();
                       const r = await api.get(`/subscriptions/${subscriptionId}/financiero/pdf`, { responseType: "blob" });
                       const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                       const a = document.createElement("a");
                       a.href = url; a.download = `informe_financiero_${subscriptionId}.pdf`; a.click();
                       window.URL.revokeObjectURL(url);
                   }}
                   className="btn-secondary text-sm flex items-center gap-2" data-testid="download-financiero-pdf">
                    Descargar informe PDF →
                </a>
            </div>

            {/* Resumen en tiempo real */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-zinc-200 mb-6">
                <Stat label="Presupuesto" value={COP(fin.presupuesto_total_cop)} mono />
                <Stat label="Gastos" value={COP(fin.total_gastos_cop)} mono />
                <Stat label="Pagado a técnicos" value={COP(fin.total_pagado_tecnicos_cop)} mono />
                <Stat label="Comisión comercial" value={COP(fin.comision_comercial?.monto_calculado_cop)} mono />
                <Stat label="Saldo disponible" value={COP(fin.saldo_disponible_cop)} mono highlight />
            </div>

            {/* Gastos generales */}
            <div className="mb-6">
                <div className="overline mb-2">Gastos generales del proyecto</div>
                <div className="border border-zinc-200">
                    {fin.gastos.length === 0 && <div className="p-3 text-sm text-zinc-400 italic">Sin gastos registrados.</div>}
                    {fin.gastos.map(g => (
                        <div key={g.gasto_id} className="flex items-center justify-between px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm">
                            <div>
                                <span className="font-semibold">{g.concepto}</span>
                                <span className="text-zinc-400 text-xs ml-2 font-mono">{g.categoria} · {g.fecha?.slice(0, 10)}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="font-mono">{COP(g.monto_cop)}</span>
                                <button onClick={() => delGasto(g.gasto_id)} className="text-red-500 hover:text-red-700 text-xs" data-testid={`del-gasto-${g.gasto_id}`}>✕</button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                    <input value={gConcepto} onChange={e => setGConcepto(e.target.value)} placeholder="Concepto (ej. Andamios)" className="input-atia flex-1 min-w-[160px]" data-testid="gasto-concepto" />
                    <select value={gCategoria} onChange={e => setGCategoria(e.target.value)} className="input-atia" data-testid="gasto-categoria">
                        <option value="materiales">Materiales</option>
                        <option value="equipos">Equipos/Alquiler</option>
                        <option value="transporte">Transporte</option>
                        <option value="permisos">Permisos/Trámites</option>
                        <option value="otro">Otro</option>
                    </select>
                    <input value={gMonto} onChange={e => setGMonto(e.target.value)} placeholder="Monto COP" type="number" className="input-atia w-36" data-testid="gasto-monto" />
                    <button onClick={addGasto} className="btn-secondary text-sm" data-testid="add-gasto">+ Agregar gasto</button>
                </div>
            </div>

            {/* Contratos y pagos a técnicos */}
            <div className="mb-6">
                <div className="overline mb-2">Valor contratado y pagos por técnico</div>
                <div className="border border-zinc-200">
                    {fin.contratos_tecnicos.length === 0 && <div className="p-3 text-sm text-zinc-400 italic">Sin técnicos contratados en este proyecto.</div>}
                    {fin.contratos_tecnicos.map(ct => (
                        <div key={ct.tecnico_cedula} className="px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm">
                            <div className="flex items-center justify-between">
                                <div>
                                    <span className="font-semibold">{ct.tecnico_nombre}</span>
                                    <span className="text-zinc-400 text-xs ml-2">{ct.tecnico_especialidad}</span>
                                </div>
                                <button onClick={() => delContrato(ct.tecnico_cedula)} className="text-red-500 hover:text-red-700 text-xs" data-testid={`del-contrato-${ct.tecnico_cedula}`}>✕ Quitar</button>
                            </div>
                            <div className="grid grid-cols-3 gap-2 mt-1 font-mono text-xs text-zinc-600">
                                <div>Contratado: <span className="font-bold text-zinc-900">{COP(ct.valor_contratado_cop)}</span></div>
                                <div>Pagado: <span className="font-bold text-zinc-900">{COP(ct.total_pagado_cop)}</span></div>
                                <div>Saldo: <span className="font-bold" style={{ color: ct.saldo_pendiente_cop > 0 ? "#B45309" : "#16A34A" }}>{COP(ct.saldo_pendiente_cop)}</span></div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                    <select value={ctTecnico} onChange={e => setCtTecnico(e.target.value)} className="input-atia flex-1 min-w-[180px]" data-testid="ct-tecnico">
                        <option value="">— Asignar valor a técnico —</option>
                        {tecnicos.map(t => <option key={t.cedula} value={t.cedula}>{t.nombre} · {t.especialidad}</option>)}
                    </select>
                    <input value={ctValor} onChange={e => setCtValor(e.target.value)} placeholder="Valor contratado COP" type="number" className="input-atia w-44" data-testid="ct-valor" />
                    <button onClick={setContrato} className="btn-secondary text-sm" data-testid="set-contrato">+ Asignar valor</button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                    <select value={pagoTecnico} onChange={e => setPagoTecnico(e.target.value)} className="input-atia flex-1 min-w-[180px]" data-testid="pago-tecnico">
                        <option value="">— Registrar pago a técnico —</option>
                        {fin.contratos_tecnicos.map(ct => <option key={ct.tecnico_cedula} value={ct.tecnico_cedula}>{ct.tecnico_nombre}</option>)}
                    </select>
                    <input value={pagoMonto} onChange={e => setPagoMonto(e.target.value)} placeholder="Monto pagado COP" type="number" className="input-atia w-40" data-testid="pago-monto" />
                    <input value={pagoConcepto} onChange={e => setPagoConcepto(e.target.value)} placeholder="Concepto (opcional)" className="input-atia flex-1 min-w-[140px]" data-testid="pago-concepto" />
                    <button onClick={addPago} className="btn-accent text-sm" data-testid="add-pago">Registrar pago →</button>
                </div>
            </div>

            {/* Comisión comercial */}
            <div>
                <div className="overline mb-2">Comisión del comercial</div>
                <div className="border border-zinc-200 p-3 text-sm mb-2">
                    {fin.comision_comercial?.comercial_nombre ? (
                        <div className="flex items-center justify-between">
                            <div><span className="font-semibold">{fin.comision_comercial.comercial_nombre}</span> · {pct(fin.comision_comercial.porcentaje)} del presupuesto</div>
                            <div className="font-mono font-bold">{COP(fin.comision_comercial.monto_calculado_cop)}</div>
                        </div>
                    ) : <div className="text-zinc-400 italic">Sin comisión asignada.</div>}
                </div>
                <div className="flex flex-wrap gap-2">
                    <select value={comComercial} onChange={e => setComComercial(e.target.value)} className="input-atia flex-1 min-w-[180px]" data-testid="com-comercial">
                        <option value="">— Comercial —</option>
                        {comerciales.map(u => <option key={u.cedula} value={u.cedula}>{u.name}</option>)}
                    </select>
                    <input value={comPorcentaje} onChange={e => setComPorcentaje(e.target.value)} placeholder="% comisión" type="number" step="0.1" min="0" max="100" className="input-atia w-32" data-testid="com-porcentaje" />
                    <button onClick={saveComision} className="btn-secondary text-sm" data-testid="save-comision">Guardar comisión</button>
                </div>
            </div>
        </div>
    );
}

async function downloadActaInicio(subscriptionId) {
    const r = await api.get(`/subscriptions/${subscriptionId}/acta-inicio/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `acta_inicio_${subscriptionId}.pdf`; a.click();
    window.URL.revokeObjectURL(url);
}

function ActaInicioModal({ subscriptionId, alcanceDefault, onClose, onSaved }) {
    const [alcance, setAlcance] = useState(alcanceDefault || "");
    const [firmaAtia, setFirmaAtia] = useState("");
    const [repNombre, setRepNombre] = useState("");
    const [repCargo, setRepCargo] = useState("");
    const [firmaCliente, setFirmaCliente] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const valido = firmaAtia && repNombre && firmaCliente;

    const submit = async () => {
        setLoading(true); setError("");
        try {
            await api.post(`/subscriptions/${subscriptionId}/acta-inicio`, {
                alcance_confirmado: alcance,
                firma_atia: firmaAtia,
                representante_cliente_nombre: repNombre,
                representante_cliente_cargo: repCargo,
                firma_cliente: firmaCliente,
            });
            onSaved();
        } catch (e) { setError(e.response?.data?.detail || "Error"); setLoading(false); }
    };

    const backdrop = useBackdropClose(onClose);
    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="acta-inicio-modal" {...backdrop.backdropProps}>
            <div className="bg-white max-w-2xl w-full max-h-[92vh] overflow-y-auto" {...backdrop.contentProps}>
                <div className="p-6 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="overline mb-1" style={{ color: "#0F766E" }}>Documento bilateral</div>
                        <h2 className="font-display text-2xl font-black tracking-tighter">Acta de Inicio de Obra</h2>
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-3xl leading-none" data-testid="close-acta-modal">×</button>
                </div>
                <div className="p-6 space-y-5">
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-300 p-3">
                        A partir de la fecha de este documento, el sistema empieza a contar el cronograma del proyecto. Requiere firma tuya y del representante del cliente presente en sitio.
                    </div>
                    <div>
                        <label className="overline block mb-2">Alcance confirmado al inicio</label>
                        <textarea value={alcance} onChange={e => setAlcance(e.target.value)} rows={3} className="input-atia w-full" data-testid="acta-alcance" />
                    </div>

                    <SignaturePad label="Firma — por ATIA Construcciones" value={firmaAtia} onChange={setFirmaAtia} testid="firma-atia-acta" />

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="overline block mb-1">Nombre del representante del cliente</label>
                            <input value={repNombre} onChange={e => setRepNombre(e.target.value)} className="input-atia w-full" data-testid="acta-rep-nombre" />
                        </div>
                        <div>
                            <label className="overline block mb-1">Cargo (opcional)</label>
                            <input value={repCargo} onChange={e => setRepCargo(e.target.value)} className="input-atia w-full" data-testid="acta-rep-cargo" />
                        </div>
                    </div>
                    <SignaturePad label="Firma — por el cliente" value={firmaCliente} onChange={setFirmaCliente} testid="firma-cliente-acta" />

                    {error && <div className="text-xs text-red-600 font-mono">{error}</div>}
                </div>
                <div className="p-6 border-t border-zinc-200 flex justify-end gap-3 sticky bottom-0 bg-white">
                    <button type="button" onClick={onClose} className="btn-secondary" data-testid="cancel-acta">Cancelar</button>
                    <button type="button" onClick={submit} disabled={!valido || loading} className="btn-accent" data-testid="confirm-acta">
                        {loading ? "Guardando…" : "Firmar y generar Acta →"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const ESTADOS_DISENO_CONFIG = {
    en_diseno: { label: "En diseño", color: "#71717A", bg: "#FAFAFA" },
    radicado: { label: "Radicado", color: "#2563EB", bg: "#EFF6FF" },
    en_curaduria: { label: "En curaduría", color: "#2563EB", bg: "#EFF6FF" },
    con_observaciones: { label: "Con observaciones", color: "#B45309", bg: "#FFFBEB" },
    aprobado: { label: "Licencia aprobada", color: "#16A34A", bg: "#F0FDF4" },
    rechazado: { label: "Rechazado", color: "#DC2626", bg: "#FEF2F2" },
};
const ENT_ESTADO_CONFIG = {
    pendiente: { label: "Pendiente", color: "#71717A", bg: "#FAFAFA" },
    en_proceso: { label: "En proceso", color: "#2563EB", bg: "#EFF6FF" },
    hecho: { label: "Hecho", color: "#16A34A", bg: "#F0FDF4" },
};

function DisenoTracking({ sub, canEdit, onChange }) {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showObsModal, setShowObsModal] = useState(false);
    const [showAddEntregable, setShowAddEntregable] = useState(false);
    const id = sub.subscription_id;
    const cfg = ESTADOS_DISENO_CONFIG[sub.estado_diseno] || ESTADOS_DISENO_CONFIG.en_diseno;
    const observaciones = sub.observaciones_curaduria || [];
    const pendientes = observaciones.filter(o => o.estado !== "subsanada");
    const entregables = sub.entregables || [];
    const hechos = entregables.filter(e => e.estado === "hecho").length;

    const resolverObs = async (obsId) => {
        const respuesta = window.prompt("Describe brevemente cómo se subsanó (opcional):") || "";
        await api.patch(`/subscriptions/${id}/diseno/observaciones/${obsId}`, { respuesta });
        onChange();
    };
    const setEstadoEntregable = async (entId, estado) => { await api.put(`/subscriptions/${id}/diseno/entregables/${entId}`, { estado }); onChange(); };
    const setFechaLimite = async (entId, fecha_limite) => { await api.put(`/subscriptions/${id}/diseno/entregables/${entId}`, { fecha_limite }); onChange(); };
    const eliminarEntregable = async (entId) => {
        if (!window.confirm("¿Quitar este entregable de la lista?")) return;
        await api.delete(`/subscriptions/${id}/diseno/entregables/${entId}`);
        onChange();
    };

    return (
        <div className="mt-2">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <span className="text-xs font-mono font-bold px-2 py-0.5 border inline-block" style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.color }}>{cfg.label}</span>
                {canEdit && (
                    <div className="flex gap-2">
                        <button onClick={() => setShowObsModal(true)} className="btn-secondary text-xs" data-testid="add-obs-btn">+ Observación de curaduría</button>
                        <button onClick={() => setShowEditModal(true)} className="btn-secondary text-xs" data-testid="edit-diseno-btn">Editar estado</button>
                    </div>
                )}
            </div>

            <div className="grid md:grid-cols-4 gap-0 border border-zinc-200 mb-6">
                <div className="p-4 border-r border-zinc-200 last:border-r-0">
                    <div className="overline mb-2">Fecha radicación</div>
                    <div className="text-sm font-mono">{sub.fecha_radicacion || "—"}</div>
                </div>
                <div className="p-4 border-r border-zinc-200 last:border-r-0">
                    <div className="overline mb-2">Número de radicado</div>
                    <div className="text-sm font-mono">{sub.numero_radicado || "—"}</div>
                </div>
                <div className="p-4 border-r border-zinc-200 last:border-r-0">
                    <div className="overline mb-2">Curaduría</div>
                    <div className="text-sm">{sub.curaduria_nombre || "—"}</div>
                </div>
                <div className="p-4">
                    <div className="overline mb-2">Presupuesto</div>
                    <div className="text-sm font-mono">{sub.presupuesto_total_cop ? COP(sub.presupuesto_total_cop) : "—"}</div>
                </div>
            </div>

            <div className="border border-zinc-200 p-4 mb-6" style={{ background: sub.coordinador_diseno_nombre ? "#F0FDFA" : "#FAFAFA" }}>
                <div className="overline mb-1">Técnico coordinador de diseño</div>
                {sub.coordinador_diseno_nombre ? (
                    <div className="text-sm font-semibold">{sub.coordinador_diseno_nombre} <span className="text-zinc-400 text-xs font-normal">· {sub.coordinador_diseno_especialidad || "General"}</span></div>
                ) : (
                    <div className="text-sm text-zinc-400 italic">Sin asignar — se encarga de articular la información de cada profesional de diseño y revisar/dejar observaciones por sistema.</div>
                )}
            </div>

            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="overline">Estudios y diseños ({hechos} de {entregables.length} completados)</div>
                    {canEdit && <button onClick={() => setShowAddEntregable(true)} className="text-xs font-semibold" style={{ color: "#0F766E" }} data-testid="add-entregable-btn">+ Agregar entregable</button>}
                </div>
                <div className="border border-zinc-200 divide-y divide-zinc-100">
                    {entregables.map(e => {
                        const ecfg = ENT_ESTADO_CONFIG[e.estado] || ENT_ESTADO_CONFIG.pendiente;
                        return (
                            <div key={e.entregable_id} className="p-3 flex items-center justify-between gap-3 text-sm flex-wrap" data-testid={`entregable-${e.entregable_id}`}>
                                <div className="flex-1 min-w-[160px]">
                                    <div className="font-medium">{e.nombre}</div>
                                    {e.fecha_limite && <div className="text-[11px] text-zinc-400 font-mono">Plazo: {e.fecha_limite}{e.fecha_completado ? ` · Completado: ${e.fecha_completado}` : ""}</div>}
                                </div>
                                {canEdit ? (
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        {["pendiente", "en_proceso", "hecho"].map(est => (
                                            <button key={est} onClick={() => setEstadoEntregable(e.entregable_id, est)}
                                                className="px-2 py-1 text-[10px] font-mono font-bold border"
                                                style={e.estado === est ? { background: ENT_ESTADO_CONFIG[est].color, borderColor: ENT_ESTADO_CONFIG[est].color, color: "#fff" } : { background: "#fff", borderColor: "#e4e4e7", color: "#71717a" }}
                                                data-testid={`entregable-${e.entregable_id}-${est}`}>
                                                {ENT_ESTADO_CONFIG[est].label}
                                            </button>
                                        ))}
                                        <input type="date" value={e.fecha_limite || ""} onChange={ev => setFechaLimite(e.entregable_id, ev.target.value)} className="input-atia text-xs w-32" title="Fecha límite" />
                                        <button onClick={() => eliminarEntregable(e.entregable_id)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                                    </div>
                                ) : (
                                    <span className="text-[10px] font-mono font-bold px-2 py-0.5 border shrink-0" style={{ color: ecfg.color, background: ecfg.bg, borderColor: ecfg.color }}>{ecfg.label}</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {observaciones.length > 0 && (
                <div>
                    <div className="overline mb-2">Observaciones de curaduría ({pendientes.length} pendiente{pendientes.length !== 1 ? "s" : ""} de {observaciones.length})</div>
                    <div className="border border-zinc-200 divide-y divide-zinc-100">
                        {observaciones.map(o => (
                            <div key={o.observacion_id} className="p-3 flex items-start justify-between gap-3 text-sm" data-testid={`obs-${o.observacion_id}`}>
                                <div className="flex-1">
                                    <div>{o.descripcion}</div>
                                    <div className="text-[11px] text-zinc-400 font-mono mt-0.5">
                                        Solicitada: {o.fecha_solicitud}
                                        {o.estado === "subsanada" && ` · Subsanada: ${o.fecha_subsanacion}`}
                                        {o.respuesta && ` · ${o.respuesta}`}
                                    </div>
                                </div>
                                {o.estado === "subsanada" ? (
                                    <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-emerald-500 bg-emerald-50 text-emerald-800 shrink-0">SUBSANADA</span>
                                ) : canEdit ? (
                                    <button onClick={() => resolverObs(o.observacion_id)} className="text-[10px] font-mono font-bold px-2 py-0.5 border border-amber-500 bg-amber-50 text-amber-800 shrink-0" data-testid={`resolver-${o.observacion_id}`}>PENDIENTE · Marcar subsanada</button>
                                ) : (
                                    <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-amber-500 bg-amber-50 text-amber-800 shrink-0">PENDIENTE</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {showEditModal && <DisenoEditModal subscriptionId={id} current={sub} onClose={() => setShowEditModal(false)} onSaved={() => { setShowEditModal(false); onChange(); }} />}
            {showObsModal && <ObsModal subscriptionId={id} onClose={() => setShowObsModal(false)} onSaved={() => { setShowObsModal(false); onChange(); }} />}
            {showAddEntregable && <AddEntregableModal subscriptionId={id} onClose={() => setShowAddEntregable(false)} onSaved={() => { setShowAddEntregable(false); onChange(); }} />}
        </div>
    );
}

function DisenoEditModal({ subscriptionId, current, onClose, onSaved }) {
    const [estado, setEstado] = useState(current.estado_diseno || "en_diseno");
    const [fechaRadicacion, setFechaRadicacion] = useState(current.fecha_radicacion || "");
    const [numeroRadicado, setNumeroRadicado] = useState(current.numero_radicado || "");
    const [curaduria, setCuraduria] = useState(current.curaduria_nombre || "");
    const [coordinadorCedula, setCoordinadorCedula] = useState(current.coordinador_diseno_cedula || "");
    const [tecnicos, setTecnicos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const backdrop = useBackdropClose(onClose);

    useEffect(() => { api.get("/technicians").then(r => setTecnicos(r.data || [])).catch(() => setTecnicos([])); }, []);

    const submit = async () => {
        setLoading(true); setError("");
        try {
            await api.put(`/subscriptions/${subscriptionId}`, {
                estado_diseno: estado, fecha_radicacion: fechaRadicacion || null, numero_radicado: numeroRadicado || null,
                curaduria_nombre: curaduria || null, coordinador_diseno_cedula: coordinadorCedula || null,
            });
            onSaved();
        } catch (e) { setError(e.response?.data?.detail || "Error"); setLoading(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" {...backdrop.backdropProps}>
            <div className="bg-white max-w-lg w-full" {...backdrop.contentProps}>
                <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
                    <h2 className="font-display text-xl font-black tracking-tighter">Editar diseño y curaduría</h2>
                    <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-3xl leading-none">×</button>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="overline block mb-1">Técnico coordinador de diseño</label>
                        <select value={coordinadorCedula} onChange={e => setCoordinadorCedula(e.target.value)} className="input-atia w-full" data-testid="select-coordinador-diseno">
                            <option value="">— Sin asignar —</option>
                            {tecnicos.map(t => <option key={t.cedula} value={t.cedula}>{t.nombre} · {t.especialidad || "General"}</option>)}
                        </select>
                        <div className="text-[11px] text-zinc-500 mt-1">Articula la información de cada profesional de diseño y deja observaciones por sistema.</div>
                    </div>
                    <div>
                        <label className="overline block mb-1">Estado</label>
                        <select value={estado} onChange={e => setEstado(e.target.value)} className="input-atia w-full" data-testid="select-estado-diseno">
                            <option value="en_diseno">En diseño</option>
                            <option value="radicado">Radicado</option>
                            <option value="en_curaduria">En curaduría</option>
                            <option value="con_observaciones">Con observaciones</option>
                            <option value="aprobado">Licencia aprobada</option>
                            <option value="rechazado">Rechazado</option>
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="overline block mb-1">Fecha radicación</label>
                            <input type="date" value={fechaRadicacion} onChange={e => setFechaRadicacion(e.target.value)} className="input-atia w-full" data-testid="input-fecha-radicacion" />
                        </div>
                        <div>
                            <label className="overline block mb-1">Número de radicado</label>
                            <input value={numeroRadicado} onChange={e => setNumeroRadicado(e.target.value)} className="input-atia w-full" data-testid="input-numero-radicado" />
                        </div>
                    </div>
                    <div>
                        <label className="overline block mb-1">Curaduría</label>
                        <input value={curaduria} onChange={e => setCuraduria(e.target.value)} placeholder="Ej. Curaduría Urbana Primera de Pereira" className="input-atia w-full" data-testid="input-curaduria" />
                    </div>
                    {error && <div className="text-xs text-red-600 font-mono">{error}</div>}
                </div>
                <div className="p-6 border-t border-zinc-200 flex justify-end gap-3">
                    <button onClick={onClose} className="btn-secondary">Cancelar</button>
                    <button onClick={submit} disabled={loading} className="btn-accent" data-testid="confirm-diseno-edit">{loading ? "Guardando…" : "Guardar →"}</button>
                </div>
            </div>
        </div>
    );
}

function ObsModal({ subscriptionId, onClose, onSaved }) {
    const [descripcion, setDescripcion] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const backdrop = useBackdropClose(onClose);

    const submit = async () => {
        if (!descripcion.trim()) return;
        setLoading(true); setError("");
        try {
            await api.post(`/subscriptions/${subscriptionId}/diseno/observaciones`, { descripcion });
            onSaved();
        } catch (e) { setError(e.response?.data?.detail || "Error"); setLoading(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" {...backdrop.backdropProps}>
            <div className="bg-white max-w-lg w-full" {...backdrop.contentProps}>
                <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
                    <h2 className="font-display text-xl font-black tracking-tighter">Observación de curaduría</h2>
                    <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-3xl leading-none">×</button>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="overline block mb-1">Descripción de la observación solicitada</label>
                        <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} rows={4} className="input-atia w-full" data-testid="input-obs-descripcion" />
                    </div>
                    {error && <div className="text-xs text-red-600 font-mono">{error}</div>}
                </div>
                <div className="p-6 border-t border-zinc-200 flex justify-end gap-3">
                    <button onClick={onClose} className="btn-secondary">Cancelar</button>
                    <button onClick={submit} disabled={!descripcion.trim() || loading} className="btn-accent" data-testid="confirm-obs">{loading ? "Guardando…" : "Agregar →"}</button>
                </div>
            </div>
        </div>
    );
}

function AddEntregableModal({ subscriptionId, onClose, onSaved }) {
    const [nombre, setNombre] = useState("");
    const [fechaLimite, setFechaLimite] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const backdrop = useBackdropClose(onClose);

    const submit = async () => {
        if (!nombre.trim()) return;
        setLoading(true); setError("");
        try {
            await api.post(`/subscriptions/${subscriptionId}/diseno/entregables`, { nombre, fecha_limite: fechaLimite || null });
            onSaved();
        } catch (e) { setError(e.response?.data?.detail || "Error"); setLoading(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" {...backdrop.backdropProps}>
            <div className="bg-white max-w-md w-full" {...backdrop.contentProps}>
                <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
                    <h2 className="font-display text-xl font-black tracking-tighter">Nuevo entregable</h2>
                    <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-3xl leading-none">×</button>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="overline block mb-1">Nombre del entregable</label>
                        <input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej. Estudio de tránsito" className="input-atia w-full" data-testid="input-entregable-nombre" />
                    </div>
                    <div>
                        <label className="overline block mb-1">Fecha límite (opcional)</label>
                        <input type="date" value={fechaLimite} onChange={e => setFechaLimite(e.target.value)} className="input-atia w-full" />
                    </div>
                    {error && <div className="text-xs text-red-600 font-mono">{error}</div>}
                </div>
                <div className="p-6 border-t border-zinc-200 flex justify-end gap-3">
                    <button onClick={onClose} className="btn-secondary">Cancelar</button>
                    <button onClick={submit} disabled={!nombre.trim() || loading} className="btn-accent" data-testid="confirm-add-entregable">{loading ? "Guardando…" : "Agregar →"}</button>
                </div>
            </div>
        </div>
    );
}
